import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react"
import { Pause, Play, Scissors, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { ConfirmActionDialog } from "@/components/confirm-action-dialog"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { CameraPathTakeSwitcher } from "@/components/locations/camera-path-take-switcher"
import { LocationSceneSpeedEditor } from "@/components/locations/location-scene-speed-editor"
import { Button } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import {
  MIN_TRIM_SPAN_MS,
  cameraPathDurationMs,
  cameraPathIsAuthored,
  hasCameraPath,
  trimCameraPath,
  type CameraPath,
} from "@/lib/location-scene-3d/camera-path"
import { displaySpeedProfile, splitSpeedProfile } from "@/lib/location-scene-3d/camera-speed"
import type { Scene3dEngine } from "@/lib/location-scene-3d/engine"
import { objectPathDurationMs, objectPathHasContent, type ObjectPath } from "@/lib/location-scene-3d/object-path"
import { savePrevizFromEngine } from "@/lib/location-scene-3d/save-previz-from-engine"
import type { SceneStateSavePayload } from "@/lib/location-scene-3d/scene-state"
import type { LibraryPrevizItem } from "@/lib/library-api"
import { cn } from "@/lib/utils"

type LocationScenePathTimelineProps = {
  engineRef: RefObject<Scene3dEngine | null>
  cameraPath: CameraPath
  playing: boolean
  recording: boolean
  /** Look pass is recording, but the playhead should still move. */
  lookRecording?: boolean
  canEdit: boolean
  /** Длина часов сцены. Без поля — длина пролёта камеры. */
  clockMs?: number
  /** Play, если есть пролёт или путь объекта. */
  motionReady?: boolean
  /** Длительность в поле правит путь объекта, пока идёт его правка или открыт его график. */
  objectPathEditing?: boolean
  /** Длительность активного пути объекта. Поле ввода открывается с ней. */
  editDurationMs?: number
  /** Путь одного выбранного объекта. Подменяет график скорости пролёта. */
  objectSpeedPath?: ObjectPath | null
  objectSpeedUid?: string | null
  projectId: number
  locationId: number | null
  /** Имя нового превиза по умолчанию: «{локация} - превиз». */
  previzName: string
  /** Snapshot 3D-сцены на Save (objects / camera / path / start). */
  getSceneStateForPreviz?: () => SceneStateSavePayload
  /** Если открыто из шота — сохранённый превиз сразу привязывается к нему. */
  shotId?: number | null
  /** Parent Scene3d — video create attaches under this layout. */
  scene3dId?: number | null
  /** Колбэк успешного сохранения превиза (обновить данные шота/библиотеки). */
  onPrevizSaved?: (previz?: LibraryPrevizItem) => void
  /** Сохранить принято, до записи ролика. Страница открывает вкладку с плиткой ожидания. */
  onPrevizCreateStart?: () => void
  /** Upload после закрытия редактора не удался — панель гасит плашку подготовки. */
  onPrevizSaveFailed?: () => void
  /** Панель шота: после записи закрыть редактор, не возвращаясь к сцене. */
  dismissAfterPrevizSave?: boolean
  onDismissAfterPrevizCapture?: () => void
  /** Save/export in progress — parent can lock modal close until upload ends. */
  onExportingChange?: (exporting: boolean) => void
  /** Canvas capture only. Parent hides editor chrome for this stretch, not the upload. */
  onCapturingChange?: (capturing: boolean) => void
  /** Движок пишет сюда тик воспроизведения/скраба (мс) — плейхед без ре-рендеров. */
  pathTimeListenerRef: RefObject<(ms: number) => void>
  /** Высота бара вместе с редактором скорости — клавиши движения садятся над ней. */
  onHeightChange?: (px: number) => void
  /** Оверлей записи вызывает Save (с обрезкой). Кнопка бара путь стирает. */
  bindSaveRef?: RefObject<(() => Promise<boolean>) | null>
}

function clamp(v: number, a: number, b: number) {
  return Math.min(b, Math.max(a, v))
}

function formatTimecode(ms: number) {
  const seconds = Math.max(0, ms) / 1000
  return `${seconds < 10 ? "0" : ""}${seconds.toFixed(1)}`
}

function parseTimecode(text: string): number | null {
  const match = /^(\d+(?:\.\d+)?)$/.exec(text.trim().replace(",", "."))
  if (!match) return null
  const seconds = Number(match[1])
  if (!Number.isFinite(seconds) || seconds >= 60) return null
  return Math.round(seconds * 1000)
}

/**
 * Таймлайн записанного пролёта: Play, скраб/trim, сброс пути.
 */
export function LocationScenePathTimeline({
  engineRef,
  cameraPath,
  playing,
  recording,
  lookRecording = false,
  canEdit,
  clockMs,
  motionReady,
  objectPathEditing = false,
  editDurationMs,
  objectSpeedPath = null,
  objectSpeedUid = null,
  projectId,
  locationId,
  previzName,
  getSceneStateForPreviz,
  shotId,
  scene3dId = null,
  onPrevizSaved,
  onPrevizCreateStart,
  onPrevizSaveFailed,
  dismissAfterPrevizSave = false,
  onDismissAfterPrevizCapture,
  onExportingChange,
  onCapturingChange,
  pathTimeListenerRef,
  onHeightChange,
  bindSaveRef,
}: LocationScenePathTimelineProps) {
  const { t } = useTranslation(["workspace"])
  const rootRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const playheadRef = useRef<HTMLDivElement>(null)
  const speedPlayheadRef = useRef<HTMLDivElement>(null)
  const timeLabelRef = useRef<HTMLSpanElement>(null)
  const currentMsRef = useRef(0)
  const [durationEdit, setDurationEdit] = useState<string | null>(null)
  const cancelDurationEditRef = useRef(false)
  const [exporting, setExporting] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [clearOpen, setClearOpen] = useState(false)
  const [clearObjectUid, setClearObjectUid] = useState<string | null>(null)
  const aliveRef = useRef(true)
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
    }
  }, [])

  const cameraDuration = cameraPathDurationMs(cameraPath)
  const pathReady = motionReady ?? hasCameraPath(cameraPath)
  const splineAuthored = cameraPathIsAuthored(cameraPath)
  const durationMs = clockMs != null ? clockMs : cameraDuration
  const [range, setRange] = useState<[number, number]>([0, durationMs])
  const [inMs, outMs] = range
  const showScrub = pathReady && !recording
  const showObjectSpeed = Boolean(canEdit && showScrub && objectSpeedPath && objectPathHasContent(objectSpeedPath))
  const showCameraSpeed = canEdit && showScrub && hasCameraPath(cameraPath) && !objectPathEditing && !showObjectSpeed
  const showSpeed = showObjectSpeed || showCameraSpeed
  const graphDurationMs =
    showObjectSpeed && objectSpeedPath
      ? objectPathDurationMs(objectSpeedPath)
      : showCameraSpeed
        ? cameraDuration
        : durationMs
  const showObjectTrim = !objectPathEditing && !showObjectSpeed && hasCameraPath(cameraPath)
  const showPlayhead = pathReady && (!recording || lookRecording)

  useEffect(() => {
    onExportingChange?.(exporting)
    return () => onExportingChange?.(false)
  }, [exporting, onExportingChange])

  useEffect(() => {
    onCapturingChange?.(capturing)
    return () => onCapturingChange?.(false)
  }, [capturing, onCapturingChange])

  const pathStamp = `${durationMs}:${cameraPath.samples.length}:${cameraPath.authoring?.points.length ?? 0}`
  // Новый/обрезанный путь — диапазон на весь путь, плейхед в начало.
  // Смена только кривой скорости путь не подменяет, плейхед остаётся.
  useEffect(() => {
    setRange([0, durationMs])
    currentMsRef.current = 0
    if (playheadRef.current) playheadRef.current.style.left = "0%"
    if (speedPlayheadRef.current) speedPlayheadRef.current.style.left = "0%"
    if (timeLabelRef.current) timeLabelRef.current.textContent = formatTimecode(0)
    // pathStamp — длина и точки, не объект пути: правка скорости не сбрасывает плейхед.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathStamp])

  useLayoutEffect(() => {
    const scrubPct = durationMs > 0 ? (currentMsRef.current / durationMs) * 100 : 0
    const graphPct = graphDurationMs > 0 ? Math.min(100, (currentMsRef.current / graphDurationMs) * 100) : 0
    if (playheadRef.current) playheadRef.current.style.left = `${scrubPct}%`
    if (speedPlayheadRef.current) speedPlayheadRef.current.style.left = `${graphPct}%`
  })

  useEffect(() => {
    pathTimeListenerRef.current = (ms) => {
      currentMsRef.current = ms
      const scrubPct = durationMs > 0 ? (ms / durationMs) * 100 : 0
      const graphPct = graphDurationMs > 0 ? Math.min(100, (ms / graphDurationMs) * 100) : 0
      if (playheadRef.current) playheadRef.current.style.left = `${scrubPct}%`
      if (speedPlayheadRef.current) speedPlayheadRef.current.style.left = `${graphPct}%`
      if (timeLabelRef.current) timeLabelRef.current.textContent = formatTimecode(ms)
    }
    return () => {
      pathTimeListenerRef.current = () => {}
    }
  }, [durationMs, graphDurationMs, pathTimeListenerRef])

  useEffect(() => {
    const el = rootRef.current
    if (!el || !onHeightChange) return
    const report = () => onHeightChange(el.offsetHeight)
    report()
    const observer = new ResizeObserver(report)
    observer.observe(el)
    return () => observer.disconnect()
  }, [onHeightChange])

  const msFromClientX = useCallback(
    (clientX: number) => {
      const rect = barRef.current?.getBoundingClientRect()
      if (!rect || rect.width <= 0) return 0
      return clamp(((clientX - rect.left) / rect.width) * durationMs, 0, durationMs)
    },
    [durationMs],
  )

  const startDrag = (e: React.PointerEvent, mode: "scrub" | "in" | "out") => {
    e.preventDefault()
    e.stopPropagation()
    const apply = (clientX: number) => {
      const ms = msFromClientX(clientX)
      if (mode === "scrub") {
        engineRef.current?.seekPath(ms)
      } else if (mode === "in") {
        setRange(([, prevOut]) => [clamp(ms, 0, prevOut - MIN_TRIM_SPAN_MS), prevOut])
      } else {
        setRange(([prevIn]) => [prevIn, clamp(ms, prevIn + MIN_TRIM_SPAN_MS, durationMs)])
      }
    }
    apply(e.clientX)
    const move = (ev: PointerEvent) => apply(ev.clientX)
    const up = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }

  const togglePlay = () => {
    const engine = engineRef.current
    if (!engine || !pathReady) return
    if (playing) {
      engine.stopPlayback()
      return
    }
    const cur = currentMsRef.current
    const fromMs = cur >= outMs - 50 || cur < inMs ? inMs : cur
    engine.playPath({ fromMs, toMs: outMs })
  }

  const trimmed = inMs > 0.5 || outMs < durationMs - 0.5
  const durationEditable = canEdit && pathReady && !recording
  const durationLabelMs =
    showObjectSpeed && objectSpeedPath
      ? objectPathDurationMs(objectSpeedPath)
      : showCameraSpeed
        ? cameraDuration
        : objectPathEditing && editDurationMs != null
          ? editDurationMs
          : durationMs

  const commitDurationEdit = (text: string) => {
    setDurationEdit(null)
    const parsed = parseTimecode(text)
    if (parsed == null) return
    if (showObjectSpeed && objectSpeedUid) engineRef.current?.setObjectPathDuration(objectSpeedUid, parsed)
    else engineRef.current?.setPathDuration(parsed)
  }

  const applyTrim = () => {
    const engine = engineRef.current
    if (!engine || !canEdit || !trimmed) return
    const next = trimCameraPath(cameraPath, inMs, outMs)
    if (next === cameraPath) return
    engine.replacePath(next)
  }

  const savePreviz = async (): Promise<boolean> => {
    const engine = engineRef.current
    if (!engine || exporting || !pathReady) return false
    onPrevizCreateStart?.()
    setExporting(true)
    setCapturing(true)
    try {
      const result = await savePrevizFromEngine({
        engine,
        projectId,
        locationId,
        scene3dId,
        previzName,
        shotId,
        getSceneState: getSceneStateForPreviz,
        fromMs: inMs,
        toMs: outMs,
        restoreView: dismissAfterPrevizSave ? false : undefined,
        onCaptured: () => {
          if (aliveRef.current) setCapturing(false)
          if (dismissAfterPrevizSave) onDismissAfterPrevizCapture?.()
        },
      })
      if (!result.ok) {
        toast.error(t("locationsPage.scene3d.timeline.exportEmpty"))
        if (aliveRef.current) onPrevizSaveFailed?.()
        return false
      }
      toast.success(t("locationsPage.scene3d.timeline.saved"))
      if (aliveRef.current) onPrevizSaved?.(result.previz)
      return true
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e))
      if (aliveRef.current) onPrevizSaveFailed?.()
      return false
    } finally {
      if (aliveRef.current) {
        setCapturing(false)
        setExporting(false)
      }
    }
  }

  if (bindSaveRef) bindSaveRef.current = savePreviz

  useEffect(() => {
    return () => {
      if (bindSaveRef) bindSaveRef.current = null
    }
  }, [bindSaveRef])

  const inPct = durationMs > 0 ? (inMs / durationMs) * 100 : 0
  const outPct = durationMs > 0 ? (outMs / durationMs) * 100 : 100

  return (
    <>
      <div ref={rootRef} className="border-t border-ui-border bg-ui-bg-elevated">
        <div className={cn("flex shrink-0 gap-2 px-2 py-2", showSpeed ? "items-start" : "items-end")}>
          <div className="flex w-max shrink-0 flex-col">
            <div className="mb-1 flex h-6 w-0 min-w-full items-center">
              <CameraPathTakeSwitcher
                engineRef={engineRef}
                cameraPath={cameraPath}
                canEdit={canEdit}
                disabled={recording || exporting || capturing}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                title={playing ? t("locationsPage.scene3d.stopPlay") : t("locationsPage.scene3d.play")}
                aria-label={playing ? t("locationsPage.scene3d.stopPlay") : t("locationsPage.scene3d.play")}
                onClick={togglePlay}
                disabled={capturing || !pathReady || recording}
              >
                {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              </Button>

              <span
                className={cn(
                  "min-w-24 shrink-0 text-center font-mono text-[11px] whitespace-nowrap text-ui-text-muted tabular-nums",
                  showSpeed ? "inline-flex h-8 items-center" : "inline",
                )}
              >
                <span ref={timeLabelRef}>{formatTimecode(pathReady ? currentMsRef.current : 0)}</span>
                {" / "}
                {durationEdit != null ? (
                  <input
                    value={durationEdit}
                    autoFocus
                    aria-label={formatTimecode(durationLabelMs)}
                    className="h-4 w-14 rounded-sm border border-ui-border bg-ui-bg-elevated px-0.5 text-center font-mono text-[11px] leading-none text-ui-text-muted tabular-nums outline-none"
                    onChange={(event) => setDurationEdit(event.target.value)}
                    onPointerDown={(event) => event.stopPropagation()}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault()
                        event.currentTarget.blur()
                      } else if (event.key === "Escape") {
                        event.preventDefault()
                        cancelDurationEditRef.current = true
                        setDurationEdit(null)
                      }
                    }}
                    onBlur={(event) => {
                      if (cancelDurationEditRef.current) {
                        cancelDurationEditRef.current = false
                        return
                      }
                      commitDurationEdit(event.currentTarget.value)
                    }}
                  />
                ) : durationEditable ? (
                  <button
                    type="button"
                    className="cursor-text"
                    onClick={() => setDurationEdit(formatTimecode(durationLabelMs))}
                  >
                    {formatTimecode(durationLabelMs)}
                  </button>
                ) : (
                  <span>{formatTimecode(pathReady ? durationMs : 0)}</span>
                )}
              </span>
            </div>
          </div>

          {showSpeed ? (
            <div className="min-w-0 flex-1">
              <LocationSceneSpeedEditor
                durationMs={graphDurationMs}
                speed={showObjectSpeed ? objectSpeedPath?.speed : cameraPath.speed}
                playheadRef={speedPlayheadRef}
                onSeek={(ms) => engineRef.current?.seekPath(ms)}
                onChange={(profile) => {
                  if (showObjectSpeed && objectSpeedUid) engineRef.current?.setObjectSpeed(objectSpeedUid, profile)
                  else engineRef.current?.setCameraSpeed(profile)
                }}
                onDuration={(ms) => {
                  if (showObjectSpeed && objectSpeedUid) engineRef.current?.setObjectPathDuration(objectSpeedUid, ms)
                  else engineRef.current?.setPathDuration(ms)
                }}
                onSplit={() => {
                  const next = splitSpeedProfile(
                    displaySpeedProfile(showObjectSpeed ? objectSpeedPath?.speed : cameraPath.speed),
                    graphDurationMs,
                    Math.min(currentMsRef.current, graphDurationMs),
                  )
                  if (!next) return
                  if (showObjectSpeed && objectSpeedUid) engineRef.current?.setObjectSpeed(objectSpeedUid, next)
                  else engineRef.current?.setCameraSpeed(next)
                }}
              />
            </div>
          ) : (
            <div className="flex h-8 min-w-0 flex-1 items-center">
              <div
                ref={barRef}
                role="slider"
                aria-label={t("locationsPage.scene3d.timeline.scrub")}
                aria-valuemin={0}
                aria-valuemax={Math.round(durationMs)}
                aria-valuenow={Math.round(currentMsRef.current)}
                aria-disabled={!showScrub}
                tabIndex={showScrub ? 0 : -1}
                className={cn(
                  "relative h-5 min-w-0 flex-1 touch-none self-center rounded-md bg-ui-surface-sunken/60",
                  showScrub ? "cursor-ew-resize" : "pointer-events-none",
                )}
                onPointerDown={showScrub ? (e) => startDrag(e, "scrub") : undefined}
              >
                {showScrub ? (
                  <div
                    className="absolute inset-y-0 rounded-md bg-ui-accent/15"
                    style={{ left: `${inPct}%`, width: `${Math.max(0, outPct - inPct)}%` }}
                  />
                ) : null}
                <div
                  ref={playheadRef}
                  className="pointer-events-none absolute inset-y-0 w-px bg-ui-accent"
                  style={{ opacity: showPlayhead ? 1 : 0 }}
                />
                {showScrub && canEdit && !splineAuthored && showObjectTrim ? (
                  <>
                    <div
                      role="slider"
                      aria-label={t("locationsPage.scene3d.timeline.trimIn")}
                      aria-valuemin={0}
                      aria-valuemax={Math.round(durationMs)}
                      aria-valuenow={Math.round(inMs)}
                      tabIndex={0}
                      className="absolute inset-y-0 z-10 w-1.5 -translate-x-1/2 cursor-ew-resize rounded bg-ui-accent/50 hover:bg-ui-accent"
                      style={{ left: `${inPct}%` }}
                      onPointerDown={(e) => startDrag(e, "in")}
                    />
                    <div
                      role="slider"
                      aria-label={t("locationsPage.scene3d.timeline.trimOut")}
                      aria-valuemin={0}
                      aria-valuemax={Math.round(durationMs)}
                      aria-valuenow={Math.round(outMs)}
                      tabIndex={0}
                      className="absolute inset-y-0 z-10 w-1.5 -translate-x-1/2 cursor-ew-resize rounded bg-ui-accent/50 hover:bg-ui-accent"
                      style={{ left: `${outPct}%` }}
                      onPointerDown={(e) => startDrag(e, "out")}
                    />
                  </>
                ) : null}
              </div>
            </div>
          )}

          {!showSpeed && showScrub && canEdit && !splineAuthored && showObjectTrim ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
              title={t("locationsPage.scene3d.timeline.trim")}
              aria-label={t("locationsPage.scene3d.timeline.trim")}
              disabled={!trimmed || capturing}
              onClick={applyTrim}
            >
              <Scissors className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          {canEdit && (showObjectSpeed || !objectPathEditing) ? (
            <ActionHintTooltip className={cn(showSpeed && "mt-7")} label={t("locationsPage.scene3d.trajectory.clear")}>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                aria-label={t("locationsPage.scene3d.timeline.reset")}
                disabled={capturing || (!showObjectSpeed && !pathReady && !recording)}
                onClick={() => {
                  setClearObjectUid(showObjectSpeed && objectSpeedUid ? objectSpeedUid : null)
                  setClearOpen(true)
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </ActionHintTooltip>
          ) : null}
          {canEdit && !recording && !exporting && (pathReady || (cameraPath.look_keys?.length ?? 0) > 0) ? (
            <span className={cn(showSpeed && "mt-7 flex h-8 items-center")}>
              <Button
                type="button"
                size="xs"
                variant="default"
                title={t("locationsPage.scene3d.timeline.save")}
                aria-label={t("locationsPage.scene3d.timeline.save")}
                onClick={() => void savePreviz()}
              >
                {t("locationsPage.scene3d.timeline.save")}
              </Button>
            </span>
          ) : null}
        </div>
      </div>
      <ConfirmActionDialog
        open={clearOpen}
        onOpenChange={setClearOpen}
        tone="danger"
        title={t(
          clearObjectUid
            ? "locationsPage.scene3d.trajectory.clearObject"
            : "locationsPage.scene3d.trajectory.clearCamera",
        )}
        message={t(
          clearObjectUid
            ? "locationsPage.scene3d.trajectory.clearObjectConfirm"
            : "locationsPage.scene3d.trajectory.clearCameraConfirm",
        )}
        onConfirm={() => {
          if (clearObjectUid) engineRef.current?.clearObjectPath(clearObjectUid)
          else engineRef.current?.clearPath()
        }}
      />
    </>
  )
}
